<script setup lang="ts">
import { watch } from 'vue'

import {
  chartColor,
  chartTime,
  dateAxis,
  formatChartDate,
  niceAxis,
  useChartJs
} from '@/shared/lib'

import type { LineChartProps } from './types'

const props = withDefaults(defineProps<LineChartProps>(), {
  dateField: 'name',
  height: 500
})

const PALETTE = [
  '--el-text-color-secondary',
  '--el-color-primary',
  '--el-color-danger',
  '--el-color-success',
  '--el-color-warning'
]

/** Gridlines of every axis coincide only if every axis is cut into this many steps. */
const STEPS = 5

const { element, render } = useChartJs({
  hasData: () => props.data.length > 0,
  draw: ({ Chart }, host) => {
    const times = props.data.map((row) => chartTime(row[props.dateField]))

    const colours = PALETTE.map((token) => chartColor(token))
    const axes = Object.fromEntries(
      props.series.map((line, index) => {
        const values = props.data.map((row) => Number(row[line.field] ?? 0))
        const { min, max, step } = niceAxis(
          Math.min(...values),
          Math.max(...values),
          STEPS
        )

        return [
          `y${index}`,
          {
            type: 'linear' as const,
            position: 'left' as const,
            min,
            max,
            ticks: { stepSize: step, color: colours[index], font: { size: 9 } },
            border: { color: colours[index], width: 2 },
            grid: { drawOnChartArea: index === 0 }
          }
        ]
      })
    )

    return new Chart(host, {
      type: 'line',
      data: {
        datasets: props.series.map((line, index) => ({
          label: line.title,
          yAxisID: `y${index}`,
          data: props.data.map((row, point) => ({
            x: times[point] ?? 0,
            y: Number(row[line.field] ?? 0)
          })),
          borderColor: colours[index],
          backgroundColor: colours[index],
          borderWidth: 1,
          // amCharts' `tensionX: 0.8`: a light curve, not the rounded one of Chart.js' own
          // monotone mode.
          tension: 0.2,
          // A single-day report is one point per line, and a line through one point draws
          // nothing at all.
          pointRadius: 2.5
        }))
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          tooltip: {
            callbacks: {
              title: (items) => formatChartDate(items[0]?.parsed.x ?? 0)
            }
          }
        },
        scales: {
          x: dateAxis(times),
          ...axes
        }
      }
    })
  }
})

watch(() => props.data, render, { flush: 'post' })
</script>

<template>
  <div class="line-chart" :style="{ height: `${height}px` }">
    <canvas ref="element" />
  </div>
</template>

<style lang="scss" scoped>
.line-chart {
  position: relative;
}
</style>
